import { useEffect, useRef } from 'react';
import { useAppSelector } from '../state/store';
import { getIsAuthenticated } from '../state/auth/authSlice';
import { getAccessToken, getLoggedInUserData } from '../state/user/userSlice';
import { IS_OTP_VERIFIED } from '../constants';
import { retrieveDataFromLocalStorage } from '../utils';
import {
  applyTicketPrefill,
  ASAP_AUTHENTICATED_BODY_CLASS,
  endZohoAsapSession,
  ensureReady,
  getPracticeIdFieldApiName,
  invokeLogin,
  isZohoAsapConfigured,
  parseAllowedRoles,
} from '../services/zohoAsap/zohoAsapClient';

const ALLOWED_ROLES = parseAllowedRoles(
  process.env.REACT_APP_ZOHO_ASAP_ALLOWED_ROLES,
);
const PRACTICE_FIELD_CONFIGURED = Boolean(getPracticeIdFieldApiName());

function buildPrefillOptions(userId: string, practiceId?: string) {
  const options = { userId };
  if (PRACTICE_FIELD_CONFIGURED && practiceId) {
    return { ...options, practiceId };
  }
  return options;
}

function shouldActivateWidget({
  isAuthenticated,
  hasOtpVerified,
  mfaRequired,
  role,
}: {
  isAuthenticated: boolean;
  hasOtpVerified: boolean;
  mfaRequired: boolean;
  role: string;
}): boolean {
  if (!isAuthenticated || !isZohoAsapConfigured()) {
    return false;
  }

  if (mfaRequired && !hasOtpVerified) {
    return false;
  }

  return ALLOWED_ROLES.includes(role);
}

const ZohoAsapBridge = () => {
  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const accessToken = useAppSelector(getAccessToken);
  const userData = useAppSelector(getLoggedInUserData);
  const { isOtpVerified, MFARequired } = useAppSelector((state) => state.auth);
  const accessTokenRef = useRef(accessToken);
  const activeRef = useRef(false);
  const initializedRef = useRef(false);
  const initializingRef = useRef(false);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    const localIsOtpVerified = retrieveDataFromLocalStorage(IS_OTP_VERIFIED);
    const hasOtpVerified = Boolean(
      isOtpVerified || localIsOtpVerified?.isOtpVerified,
    );
    const role = userData?.role || '';
    const active = shouldActivateWidget({
      isAuthenticated,
      hasOtpVerified,
      mfaRequired: MFARequired,
      role,
    });

    if (!active) {
      if (activeRef.current) {
        activeRef.current = false;
        initializedRef.current = false;
        initializingRef.current = false;
        void endZohoAsapSession();
      }
      return;
    }

    activeRef.current = true;

    if (initializedRef.current || initializingRef.current) {
      return;
    }

    let cancelled = false;
    initializingRef.current = true;

    const activate = async () => {
      try {
        await ensureReady();
        if (cancelled) {
          return;
        }

        await invokeLogin(() => accessTokenRef.current);

        if (userData.userId) {
          applyTicketPrefill(
            buildPrefillOptions(userData.userId, userData.practiceId),
          );
        }

        document.body.classList.add(ASAP_AUTHENTICATED_BODY_CLASS);
        initializedRef.current = true;
      } catch (error) {
        if (!cancelled) {
          initializedRef.current = false;
          if (process.env.NODE_ENV === 'development') {
            console.warn('Zoho ASAP activation failed', error);
          }
        }
      } finally {
        if (!cancelled) {
          initializingRef.current = false;
        }
      }
    };

    void activate();

    return () => {
      cancelled = true;
      initializingRef.current = false;
    };
  }, [MFARequired, isAuthenticated, isOtpVerified, userData.role]);

  useEffect(() => {
    if (!initializedRef.current || !userData.userId) {
      return;
    }

    applyTicketPrefill(
      buildPrefillOptions(userData.userId, userData.practiceId),
    );
  }, [
    userData.userId,
    ...(PRACTICE_FIELD_CONFIGURED ? [userData.practiceId] : []),
  ]);

  return null;
};

export default ZohoAsapBridge;
